import { ErrorMessage } from "@hookform/error-message";
import { Input } from "@mantine/core";
import {
  type FileWithPath,
  IMAGE_MIME_TYPE,
  Dropzone as MantineDropzone,
} from "@mantine/dropzone";
import Image from "next/image";
import { useState } from "react";
import {
  type Control,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";

import {
  Avatar,
  FileInput,
  Group,
  Stack,
  Text,
} from "@/components/ui/elements";

import classes from "./_css/Dropzone.module.css";

type Props<FORM_TYPE extends FieldValues> = {
  control: Control<FORM_TYPE>;
  name: FieldPath<FORM_TYPE>;
  errors?: FieldErrors;
  /**
   * 選択直後にファイルを加工してからフォームへ渡したいときに使う。
   * 返した値がフォームの値になり、空文字を返すと選択前の表示に戻す
   */
  onSelectFile?: (file: FileWithPath) => Promise<string>;
};

export function Dropzone<FORM_TYPE extends FieldValues>(
  props: Props<FORM_TYPE>,
) {
  const { control, name, errors, onSelectFile } = props;
  const { field } = useController({
    name,
    control,
  });
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const hasImage = field.value || files.length;

  const Previews = () => {
    // HEICのようにブラウザが描画できない形式は、壊れた画像の代わりにファイル名を出す
    if (previewFailed && files.length) {
      return (
        <Group className={classes.image} justify="center">
          <Stack align="center">
            <Text fw={500}>{files[0].name}</Text>
            <Text size="sm">選択済み</Text>
          </Stack>
        </Group>
      );
    }

    if (field.value && !files.length) {
      return (
        <Group className={classes.image} justify="center">
          <Image
            src={field.value}
            alt="image"
            fill
            onError={() => setPreviewFailed(true)}
          />
        </Group>
      );
    }
    return files.map((file, index) => {
      const imageUrl = URL.createObjectURL(file);
      return (
        <Group className={classes.image} justify="center" key={index}>
          <Image
            src={imageUrl}
            alt="image"
            fill
            onError={() => setPreviewFailed(true)}
          />
        </Group>
      );
    });
  };
  const handleDrop = async (droppedFiles: FileWithPath[]) => {
    setPreviewFailed(false);
    if (!onSelectFile) {
      setFiles(droppedFiles);
      field.onChange(droppedFiles);
      return;
    }

    // 加工には時間がかかるため、終わるまで選択できないようにする
    setPreparing(true);
    try {
      const value = await onSelectFile(droppedFiles[0]);
      setFiles(value ? [droppedFiles[0]] : []);
      field.onChange(value);
    } catch {
      // 加工側でエラーを表示する想定だが、失敗しても選択状態が残らないようにする
      setFiles([]);
      field.onChange("");
    } finally {
      setPreparing(false);
    }
  };
  // 既存のIMAGE_MIME_TYPEに'image/heic'を追加
  const CUSTOM_IMAGE_MIME_TYPE = [...IMAGE_MIME_TYPE, "image/heic"];
  return (
    <div>
      <MantineDropzone
        accept={CUSTOM_IMAGE_MIME_TYPE}
        loading={preparing}
        onDrop={(file) => handleDrop(file)}
      >
        <Group justify="center">
          <FileInput
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            ref={field.ref}
            disabled
            style={{ display: "none" }}
          />
          {!hasImage && (
            <Stack align="center">
              <Avatar color="gray" />
              <Text>画像を選択してください</Text>
            </Stack>
          )}
          <Previews />
        </Group>
      </MantineDropzone>
      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }) => <Input.Error>{message}</Input.Error>}
      />
    </div>
  );
}
